import { useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';
import {
  getSitrepCollabTransport,
  releaseSitrepCollabTransport,
} from './sitrepCollabTransport';

export const SITREP_YJS_REMOTE_ORIGIN = 'sitrep-remote';

function encodeUpdate(update: Uint8Array): string {
  let binary = '';
  update.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

function decodeUpdate(encoded: string): Uint8Array {
  const binary = atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Per-section Y.Doc synced over sitrep collab transport. Returns null ydoc
 * until the room is subscribed.
 */
export function useSitrepSectionYjs(params: {
  sitrepId: string;
  sectionId: string;
  enabled: boolean;
}) {
  const { sitrepId, sectionId, enabled } = params;
  const [ydoc, setYdoc] = useState<Y.Doc | null>(null);
  const docRef = useRef<Y.Doc | null>(null);

  useEffect(() => {
    if (!enabled || !sitrepId || !sectionId) {
      setYdoc(null);
      return;
    }

    const transport = getSitrepCollabTransport(sitrepId);
    const topic = `sitrep-yjs:${sitrepId}:${sectionId}`;
    const doc = new Y.Doc();
    docRef.current = doc;
    setYdoc(doc);

    let updateHandler: ((update: Uint8Array, origin: unknown) => void) | null =
      null;

    const unsub = transport.onMessage((envelope) => {
      if (envelope.topic !== topic) return;
      if (envelope.type !== 'yjs-update' && envelope.type !== 'yjs-sync')
        return;
      const encoded = envelope.payload.update;
      if (typeof encoded !== 'string' || !encoded) return;
      try {
        const update = decodeUpdate(encoded);
        if (update.length === 0) return;
        Y.applyUpdate(doc, update, SITREP_YJS_REMOTE_ORIGIN);
      } catch (err) {
        console.error('[SitrepCollab] failed to apply yjs update', err);
      }
    });

    void (async () => {
      await transport.subscribe([topic]);
      // Ask peers for a full state sync.
      await transport.publish(topic, 'yjs-sync-request', {});

      updateHandler = (update: Uint8Array, origin: unknown) => {
        if (origin === SITREP_YJS_REMOTE_ORIGIN) return;
        if (update.length === 0) return;
        void transport.publish(topic, 'yjs-update', {
          update: encodeUpdate(update),
        });
      };
      doc.on('update', updateHandler);
    })();

    const syncRequestUnsub = transport.onMessage((envelope) => {
      if (envelope.topic !== topic || envelope.type !== 'yjs-sync-request') {
        return;
      }
      const state = Y.encodeStateAsUpdate(doc);
      if (state.length === 0) return;
      void transport.publish(topic, 'yjs-sync', {
        update: encodeUpdate(state),
      });
    });

    return () => {
      if (updateHandler) doc.off('update', updateHandler);
      unsub();
      syncRequestUnsub();
      void transport.unsubscribe([topic]);
      releaseSitrepCollabTransport(sitrepId);
      doc.destroy();
      docRef.current = null;
      setYdoc(null);
    };
  }, [enabled, sitrepId, sectionId]);

  return { ydoc };
}
